import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import OffCanvas, {
	OffCanvasBody,
	OffCanvasHeader,
} from '../bootstrap/OffCanvas';
import Icon from '../icon/Icon';
import Spinner from '../bootstrap/Spinner';
import notificationService, { INotificationItem } from '../../services/notificationService';
import resolveNotificationRoute from '../../helpers/notificationRouteResolver';
import './NotificationDrawer.scss';

interface INotificationDrawerProps {
	isOpen: boolean;
	setOpen: (open: boolean) => void;
	unreadCount: number;
	setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
}

const formatRelativeTime = (rawDate?: string | number | Date | null): string => {
	if (!rawDate) return '';
	const d = dayjs(rawDate);
	if (!d.isValid()) return String(rawDate);
	const now = dayjs();
	const diffSec = Math.abs(now.diff(d, 'second'));
	if (diffSec < 60) return 'Just now';
	const diffMin = Math.abs(now.diff(d, 'minute'));
	if (diffMin < 60) return `${diffMin}m ago`;
	const diffHour = Math.abs(now.diff(d, 'hour'));
	if (diffHour < 24) return `${diffHour}h ago`;
	const diffDay = Math.abs(now.diff(d, 'day'));
	if (diffDay < 7) return `${diffDay}d ago`;
	return d.format('MMM D');
};

const getNotificationVisuals = (item: INotificationItem) => {
	const text = `${item.title || ''} ${item.message || ''} ${item.type || ''} ${item.notification_type || ''}`.toLowerCase();

	if (
		text.includes('fail') ||
		text.includes('reject') ||
		text.includes('cancel') ||
		text.includes('error') ||
		text.includes('danger')
	) {
		return {
			icon: 'GppBad' as const,
			boxClass: 'notif-icon-failed',
		};
	}

	if (
		text.includes('approv') ||
		text.includes('success') ||
		text.includes('paid') ||
		text.includes('complet') ||
		text.includes('verified')
	) {
		return {
			icon: 'GppGood' as const,
			boxClass: 'notif-icon-success',
		};
	}

	if (text.includes('pending') || text.includes('warn') || text.includes('review')) {
		return {
			icon: 'GppMaybe' as const,
			boxClass: 'notif-icon-warning',
		};
	}

	return {
		icon: 'Notifications' as const,
		boxClass: 'notif-icon-info',
	};
};

export const NotificationDrawer: FC<INotificationDrawerProps> = ({
	isOpen,
	setOpen,
	unreadCount,
	setUnreadCount,
}) => {
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState<'unread' | 'all'>('unread');
	const [items, setItems] = useState<INotificationItem[]>([]);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [page, setPage] = useState<number>(1);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
	const [hasMore, setHasMore] = useState<boolean>(true);
	const [isMarkingAll, setIsMarkingAll] = useState<boolean>(false);

	const scrollContainerRef = useRef<HTMLDivElement | null>(null);

	const limit = 10;

	// Fetch page 1 when drawer opens or tab changes
	const fetchInitial = useCallback(
		async (tabToFetch: 'unread' | 'all') => {
			setIsLoading(true);
			setPage(1);
			try {
				const filter = tabToFetch === 'unread' ? false : undefined;
				const res = await notificationService.getAll({
					page: 1,
					limit,
					is_read: filter,
				});

				setItems(res.list);
				setTotalCount(res.total);
				setHasMore(res.list.length < res.total);

				if (typeof res.unreadCount === 'number') {
					setUnreadCount(res.unreadCount);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Failed to load notifications:', error);
				setItems([]);
				setTotalCount(0);
				setHasMore(false);
			} finally {
				setIsLoading(false);
			}
		},
		[setUnreadCount],
	);

	useEffect(() => {
		if (isOpen) {
			fetchInitial(activeTab);
		}
	}, [isOpen, activeTab, fetchInitial]);

	// Load next page on scroll
	const loadNextPage = useCallback(async () => {
		if (isLoadingMore || isLoading || !hasMore) return;
		setIsLoadingMore(true);
		const nextPage = page + 1;
		try {
			const filter = activeTab === 'unread' ? false : undefined;
			const res = await notificationService.getAll({
				page: nextPage,
				limit,
				is_read: filter,
			});

			setItems((prev) => {
				const existingIds = new Set(prev.map((i) => String(i.id)));
				const filteredNew = res.list.filter((i) => !existingIds.has(String(i.id)));
				const combined = [...prev, ...filteredNew];
				setHasMore(combined.length < res.total);
				return combined;
			});
			setTotalCount(res.total);
			setPage(nextPage);

			if (typeof res.unreadCount === 'number') {
				setUnreadCount(res.unreadCount);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Failed to load more notifications:', error);
			setHasMore(false);
		} finally {
			setIsLoadingMore(false);
		}
	}, [isLoadingMore, isLoading, hasMore, page, activeTab, setUnreadCount]);

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const target = e.currentTarget;
		if (target.scrollHeight - target.scrollTop - target.clientHeight < 60) {
			loadNextPage();
		}
	};

	// Mark all notifications as read
	const handleMarkAllRead = async () => {
		if (isMarkingAll || unreadCount === 0) return;
		setIsMarkingAll(true);
		try {
			await notificationService.markAllRead();
			setUnreadCount(0);

			if (activeTab === 'unread') {
				setItems([]);
				setTotalCount(0);
				setHasMore(false);
			} else {
				setItems((prev) => prev.map((item) => ({ ...item, is_read: true })));
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Failed to mark all as read:', error);
		} finally {
			setIsMarkingAll(false);
		}
	};

	// Mark single notification as read & navigate to the exact screen
	const handleNotificationClick = async (item: INotificationItem) => {
		if (!item.is_read) {
			try {
				await notificationService.update(item.id, { is_read: true });
				setUnreadCount((prev) => Math.max(0, prev - 1));

				if (activeTab === 'unread') {
					setItems((prev) => prev.filter((i) => String(i.id) !== String(item.id)));
					setTotalCount((prev) => Math.max(0, prev - 1));
				} else {
					setItems((prev) =>
						prev.map((i) =>
							String(i.id) === String(item.id) ? { ...i, is_read: true } : i,
						),
					);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Failed to update notification:', error);
			}
		}

		// Always close the notification drawer
		setOpen(false);

		// Resolve destination route
		const resolvedRoute = resolveNotificationRoute({
			...item,
			...(item.data && typeof item.data === 'object' ? item.data : {}),
			data: item.data,
		});

		const targetRoute =
			resolvedRoute && resolvedRoute !== '/'
				? resolvedRoute
				: item.url || item.route || null;

		if (targetRoute) {
			if (window.location.pathname !== targetRoute) {
				navigate(targetRoute);
			} else {
				// Already on target screen: trigger reload so updated records appear
				window.location.reload();
			}
		}
	};

	return (
		<OffCanvas
			id='notificationCanvas'
			titleId='notificationCanvasTitle'
			placement='end'
			className='notification-offcanvas'
			isOpen={isOpen}
			setOpen={setOpen}>
			<OffCanvasHeader setOpen={setOpen}>
				<div className='notif-header-title'>
					<h5>Notifications</h5>
					{unreadCount > 0 && (
						<span className='notif-badge-new'>{unreadCount} new</span>
					)}
				</div>
			</OffCanvasHeader>

			<OffCanvasBody>
				{/* Sub-header: Tabs & Mark All As Read */}
				<div className='notif-sub-bar'>
					<div className='notif-tabs'>
						<button
							type='button'
							className={`notif-tab-btn ${activeTab === 'unread' ? 'active' : ''}`}
							onClick={() => setActiveTab('unread')}>
							Unread {unreadCount > 0 ? `(${unreadCount})` : '(0)'}
						</button>
						<button
							type='button'
							className={`notif-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
							onClick={() => setActiveTab('all')}>
							All
						</button>
					</div>

					<button
						type='button'
						className='notif-mark-read-btn'
						disabled={unreadCount === 0 || isMarkingAll}
						onClick={handleMarkAllRead}>
						{isMarkingAll ? 'Marking...' : 'Mark all as read'}
					</button>
				</div>

				{/* Scrollable Notification List */}
				<div
					ref={scrollContainerRef}
					className='notif-list-container'
					onScroll={handleScroll}>
					{isLoading && items.length === 0 && (
						<div className='text-center py-5'>
							<Spinner isGrow={false} />
						</div>
					)}

					{!isLoading && items.length === 0 && (
						<div className='notif-empty-state'>
							<Icon icon='NotificationsNone' className='notif-empty-icon' />
							<h6>No notifications</h6>
							<p>
								{activeTab === 'unread'
									? "You're all caught up! No unread notifications."
									: 'There are no notifications to display.'}
							</p>
						</div>
					)}

					{items.map((item) => {
						const visuals = getNotificationVisuals(item);
						return (
							<div
								key={String(item.id)}
								role='button'
								tabIndex={0}
								className={`notif-card ${!item.is_read ? 'is-unread' : ''}`}
								onClick={() => handleNotificationClick(item)}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										handleNotificationClick(item);
									}
								}}>
								{!item.is_read && <span className='notif-dot' />}

								<div className={`notif-icon-box ${visuals.boxClass}`}>
									<Icon icon={visuals.icon} size='lg' />
								</div>

								<div className='notif-card-content'>
									<div
										className={`notif-card-text ${
											!item.is_read ? 'is-unread-text' : ''
										}`}>
										{item.message || item.title}
									</div>
									<div className='notif-card-time'>
										<Icon icon='AccessTime' size='sm' />
										<span>{formatRelativeTime(item.created_at)}</span>
									</div>
								</div>
							</div>
						);
					})}
				</div>

				{/* Sticky Footer */}
				{!isLoading && items.length > 0 && (
					<div className='notif-footer-bar'>
						<span>
							Showing {items.length} of {totalCount} notifications
						</span>

						{hasMore ? (
							<button
								type='button'
								className='notif-scroll-chip'
								disabled={isLoadingMore}
								onClick={loadNextPage}>
								{isLoadingMore ? (
									<>
										<Spinner isSmall isGrow={false} />
										<span>Loading...</span>
									</>
								) : (
									<span>Scroll for more</span>
								)}
							</button>
						) : (
							<span className='text-muted small'>All loaded</span>
						)}
					</div>
				)}
			</OffCanvasBody>
		</OffCanvas>
	);
};

export default NotificationDrawer;
